import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Package,
  CalendarClock,
  RefreshCw,
  Zap,
  CreditCard,
  Fingerprint,
  Percent,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useT } from '../i18n/LanguageContext'
import { useDocumentMeta } from '../hooks/useDocumentMeta'
import type { Dictionary } from '../i18n/types'

type SellKey = keyof Omit<Dictionary['pluginCommerce']['sells'], 'eyebrow'>
type FeatureKey = keyof Omit<Dictionary['pluginCommerce']['features'], 'eyebrow'>

const sellList: Array<{ key: SellKey; icon: LucideIcon }> = [
  { key: 'products', icon: Package },
  { key: 'services', icon: CalendarClock },
  { key: 'recurring', icon: RefreshCw },
]

const featureList: Array<{ key: FeatureKey; icon: LucideIcon }> = [
  { key: 'integration', icon: Zap },
  { key: 'payments', icon: CreditCard },
  { key: 'brandFit', icon: Fingerprint },
  { key: 'noInvestment', icon: Percent },
]

export default function PluginCommerce() {
  const t = useT()
  const pc = t.pluginCommerce
  useDocumentMeta(pc.meta.title, pc.meta.description)

  return (
    <section className="relative pt-40 pb-28 min-h-screen overflow-hidden">
      <div className="absolute inset-0 bg-grid" aria-hidden="true" />
      <div className="absolute inset-0 bg-radial-top" aria-hidden="true" />
      <div
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-commerce-500/[0.06] rounded-full blur-[130px]"
        aria-hidden="true"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Hero */}
        <motion.header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <Link
            to="/#product"
            className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            {pc.backToProducts}
          </Link>
          <p className="font-mono text-commerce-400 text-xs tracking-[0.2em] uppercase mb-5">
            {pc.eyebrow}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight leading-[1.05] mb-6">
            {pc.titlePart1}{' '}
            <span className="text-gradient">{pc.titlePart2}</span>
          </h1>
          <p className="text-lg text-zinc-400 leading-relaxed mb-10">
            {pc.intro}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/plugin-commerce/demo"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-commerce-500 hover:bg-commerce-600 rounded-xl text-sm font-semibold text-white transition-all"
            >
              {pc.ctaDemo}
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] rounded-xl text-sm font-semibold text-white transition-all"
            >
              {pc.ctaContact}
            </Link>
          </div>
        </motion.header>

        {/* Co sprzedasz */}
        <div className="mt-24">
          <p className="font-mono text-brand-400 text-xs tracking-[0.2em] uppercase mb-8">
            {pc.sells.eyebrow}
          </p>
          <div className="grid sm:grid-cols-3 gap-5">
            {sellList.map((item, i) => {
              const Icon = item.icon
              const copy = pc.sells[item.key]
              return (
                <motion.div
                  key={item.key}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-7"
                >
                  <div className="w-12 h-12 rounded-xl border border-commerce-500/30 bg-commerce-500/10 flex items-center justify-center mb-5">
                    <Icon size={22} className="text-commerce-400" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-white tracking-tight mb-2">
                    {copy.title}
                  </h3>
                  <p className="text-zinc-400 leading-relaxed text-sm">
                    {copy.desc}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>

        {/* Dlaczego to działa */}
        <div className="mt-20">
          <p className="font-mono text-brand-400 text-xs tracking-[0.2em] uppercase mb-8">
            {pc.features.eyebrow}
          </p>
          <div className="grid sm:grid-cols-2 gap-5">
            {featureList.map((item, i) => {
              const Icon = item.icon
              const copy = pc.features[item.key]
              return (
                <motion.div
                  key={item.key}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="flex gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6"
                >
                  <div className="w-11 h-11 rounded-xl border border-brand-500/30 bg-brand-500/10 flex items-center justify-center shrink-0">
                    <Icon size={20} className="text-brand-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1.5">
                      {copy.title}
                    </h3>
                    <p className="text-zinc-400 leading-relaxed text-sm">
                      {copy.desc}
                    </p>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>

        {/* Domknięcie: powrót do demo */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="mt-20 rounded-2xl border border-commerce-500/20 bg-commerce-500/[0.06] p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center gap-6"
        >
          <p className="flex-1 text-lg text-white/90 leading-relaxed">
            {pc.demoInvite}
          </p>
          <Link
            to="/plugin-commerce/demo"
            className="inline-flex items-center gap-2 self-start sm:self-auto px-7 py-3.5 bg-commerce-500 hover:bg-commerce-600 rounded-xl text-sm font-semibold text-white transition-all shrink-0"
          >
            {pc.ctaDemo}
            <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}
